/*
 * File: ResetPassword.jsx
 * Project: frontend
 * File Created: Monday, 1st May 2023 5:07:21 pm
 * Author: Sharath Puranik (sharath.puranik@stylumia.com)
 * -----
 * Last Modified: Friday, 30th August 2024 1:12:56 am
 * Modified By: Sharath Puranik (sharath.puranik@stylumia.com>)
 * -----
 * Copyright - 2024, Stylumia Intelligence Technology Pvt Ltd
 */

import React, { Component } from "react";
import axios from "axios";
import Lottie from "lottie-react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  AUTH_CONSTANTS,
  password_reset_endpoint,
  password_validate
} from "../../constants/constants";
import get_host from "../../utils";
import sadDog from "../../animations/sad-dog.json";
import { usersAuthLog } from "../../utils/loginLogs";
import Input from "../Input/index";
import { LoggerContext } from "../../loggers";
import {
  MIXPANEL_RESET_PASSWORD_EVENT,
  MIXPANEL_RESET_PASSWORD_PAGE_NAME
} from "../../loggers/constant";

import LockIcon from "../../assests/lock.png";

import { useDeviceIdentification } from "../../hooks/useDeviceIdentification";

import Template from "./template";

import "./style.css";

class ResetPassword extends Component {
  static contextType = LoggerContext;

  constructor(props) {
    super(props);
    const search = props.location.search;
    const email = new URLSearchParams(search).get("email");
    const token = new URLSearchParams(search).get("token");
    const redirect = new URLSearchParams(search).get("redirect");

    this.state = {
      email,
      token,
      redirect,
      password: "",
      finalError: false,
      confirmPassword: "",
      inputPasswordType: "password",
      inputPasswordIcon: "showPassword.svg",
      passwordResetMessage: "",
      mainLoading: 0,
      status_code: "",
      backendBroke: false,
      error: { email: false, text: false },
      loading: false,
      err_message: "",
      activeFingerprintId: null
    };

    this.getPassword = this.getPassword.bind(this);
    this.getConfirmPassword = this.getConfirmPassword.bind(this);
    this.showPassword = this.showPassword.bind(this);
    this.handleSubmitLogin = this.handleSubmitLogin.bind(this);
    this.handleResetPassword = this.handleResetPassword.bind(this);
    this.handleGoToMainLogin = this.handleGoToMainLogin.bind(this);
  }

  handleGoToMainLogin() {
    this.props.navigate(`/?redirect=${this.state.redirect}`);
  }

  componentDidMount() {
    this.context.mixpanel.pageView(MIXPANEL_RESET_PASSWORD_PAGE_NAME);
    this.setState({ mainLoading: 1 });

    const fingerIdFromStorage = localStorage.getItem("fingerId");

    if (!fingerIdFromStorage) {
      console.error(
        "ResetPassword: No fingerprintId found in localStorage. This indicates a broken flow."
      );
      this.setState({
        mainLoading: 3,
        status_code: 400,
        backendBroke: true,
        err_message:
          "Device identification missing. Please try requesting the reset link again.",
        activeFingerprintId: null
      });
      this.context.mixpanel.log(true, MIXPANEL_RESET_PASSWORD_EVENT, {
        email: this.state.email,
        fingerprintID: "MISSING_FROM_STORAGE",
        error: "No fingerprintId in localStorage on mount",
        step: "validate_link_mount"
      });
      return;
    }

    this.setState({ activeFingerprintId: fingerIdFromStorage });

    const formData = new FormData();
    formData.append("email", this.state.email);
    formData.append("redirect", this.state.redirect);
    formData.append("token", this.state.token);
    formData.append("fingerprint", fingerIdFromStorage);

    const start = performance.now();
    axios
      .post(get_host(password_validate), formData)
      .then((res) => {
        if (res.status === 200) {
          this.setState({
            mainLoading: 2,
            status_code: res.status
          });
          usersAuthLog({
            email: this.state.email,
            serviceCategory: "Auth",
            service: "Login",
            eventCategory: "Reset password",
            eventAction: "Validate",
            eventLabel: "Password Reset Link Validated",
            fingerprintID: fingerIdFromStorage,
            eventValue: {
              status_code: res.status,
              time_taken: performance.now() - start,
              fingerprint: fingerIdFromStorage
            }
          });
        }
      })
      .catch((err) => {
        const statusCode = err.response ? err.response.status : 500;
        const errorMessage =
          (err.response && err.response.data && err.response.data.detail) ||
          "Link validation failed";
        this.setState({
          mainLoading: 3,
          status_code: statusCode,
          err_message: errorMessage
        });

        let eventLabel = errorMessage;
        if (statusCode === 401 || statusCode === 400) {
          eventLabel = `Password Reset Link Expired`;
        } else if (statusCode === 409) {
          eventLabel = `Different Browser/Device Detected`;
        }

        usersAuthLog({
          email: this.state.email,
          serviceCategory: "Auth",
          service: "Login",
          eventCategory: "Reset password",
          eventAction: "Validate",
          eventLabel,
          fingerprintID: fingerIdFromStorage,
          eventValue: {
            status_code: statusCode,
            time_taken: performance.now() - start,
            fingerprint: fingerIdFromStorage
          }
        });
        this.context.mixpanel.log(true, MIXPANEL_RESET_PASSWORD_EVENT, {
          email: this.state.email,
          fingerprintID: fingerIdFromStorage,
          error: eventLabel,
          step: "validate_link_api_error"
        });
      });
  }

  handleError = (errorState, er) => {
    let x;
    this.setState(
      (state) => {
        x = state.error;
        x[errorState] = er;
        return { error: x };
      },
      () => {
        let flag = false;
        for (const key in this.state.error) {
          if (this.state.error[key]) {
            flag = true;
            break;
          }
        }
        if (
          flag ||
          (this.state.password &&
            this.state.confirmPassword &&
            this.state.password !== this.state.confirmPassword)
        ) {
          this.setState({ finalError: true });
        } else {
          this.setState({ finalError: false });
        }
      }
    );
  };

  getPassword({ value }) {
    this.setState({ password: value, err_message: "" }, () =>
      this.handleError("password", !value || value.length < 5)
    );
  }

  getConfirmPassword({ value }) {
    this.setState({ confirmPassword: value, err_message: "" }, () =>
      this.handleError("confirmPassword", !value || value.length < 5)
    );
  }

  showPassword() {
    if (this.state.inputPasswordType === "password") {
      this.setState({
        inputPasswordType: "text",
        inputPasswordIcon: "show-password-enabled.svg"
      });
    } else {
      this.setState({
        inputPasswordType: "password",
        inputPasswordIcon: "showPassword.svg"
      });
    }
  }

  handleSubmitLogin() {
    const {
      activeFingerprintId,
      email,
      password,
      redirect,
      token,
      confirmPassword
    } = this.state;

    if (password !== confirmPassword) {
      this.setState({ err_message: "Passwords do not match.", loading: false });
      return;
    }
    if (!password || password.length < 5) {
      this.setState({
        err_message: "Password should be at least 5 characters long.",
        loading: false
      });
      return;
    }

    if (!activeFingerprintId) {
      console.error(
        "ResetPassword: activeFingerprintId is not set for handleSubmitLogin."
      );
      this.setState({
        loading: false,
        err_message:
          "Device identification is missing. Please refresh and try again or request a new link.",
        backendBroke: true
      });
      this.context.mixpanel.log(true, MIXPANEL_RESET_PASSWORD_EVENT, {
        email: this.state.email,
        fingerprintID: "MISSING_FOR_SUBMIT_FROM_STATE",
        error: "activeFingerprintId not set in state for submit",
        step: "submit_reset_precheck"
      });
      return;
    }

    if (email && password) {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("redirect", redirect);
      formData.append("password", password);
      formData.append("token", token);
      formData.append("fingerprint", activeFingerprintId);

      this.setState({ loading: true, err_message: "" });
      const start = performance.now();
      axios
        .post(get_host(password_reset_endpoint), formData)
        .then((res) => {
          if (res.status === 200) {
            this.setState({
              err_message: "",
              loading: false,
              passwordResetMessage: "Password reset successfully."
            });
            usersAuthLog({
              email: this.state.email,
              serviceCategory: "Auth",
              service: "Login",
              eventCategory: "Reset password",
              eventAction: "Submit",
              eventLabel: "Password Reset Successful",
              fingerprintID: activeFingerprintId,
              eventValue: {
                status_code: res.status,
                time_taken: performance.now() - start,
                fingerprint: activeFingerprintId
              }
            });
            this.context.mixpanel.log(false, MIXPANEL_RESET_PASSWORD_EVENT, {
              email: this.state.email,
              fingerprintID: activeFingerprintId,
              success: true,
              step: "submit_reset_success"
            });
            this.props.navigate(
              `/login/password-reset-success?email=${this.state.email}&redirect=${this.state.redirect}`
            );
          }
        })
        .catch((err) => {
          const statusCode = err.response ? err.response.status : 500;
          const errorMessage =
            (err.response &&
              err.response.data &&
              (err.response.data.detail || err.response.data.desccription)) ||
            "Password reset failed.";
          this.setState({
            err_message: errorMessage,
            loading: false,
            passwordResetMessage:
              "There was some problem while resetting the password."
          });
          usersAuthLog({
            email: this.state.email,
            serviceCategory: "Auth",
            service: "Login",
            eventCategory: "Reset password",
            eventAction: "Submit",
            eventLabel:
              (err.response && err.response.data && err.response.data.detail) ||
              "Password Reset Failed",
            fingerprintID: activeFingerprintId,
            eventValue: {
              status_code: statusCode,
              time_taken: performance.now() - start,
              fingerprint: activeFingerprintId
            }
          });
          this.context.mixpanel.log(true, MIXPANEL_RESET_PASSWORD_EVENT, {
            email: this.state.email,
            fingerprintID: activeFingerprintId,
            error: errorMessage,
            step: "submit_reset_api_error"
          });
        })
        .finally(() => {});
    } else {
      this.setState({
        loading: false,
        err_message: "Email or password cannot be empty."
      });
    }
  }

  handleResetPassword() {
    const search = this.props.location.search;
    const redirect = new URLSearchParams(search).get("redirect");
    this.props.navigate(`/login/forgot?redirect=${redirect}`);
  }

  handleRedirect = () => {
    this.props.navigate(
      `/login/magic?email=${this.state.email}&redirect=${this.state.redirect}`
    );
  };

  render() {
    const backendBroke =
      "Something seems to be broken. Please try after some time.";

    const isResetDisabled =
      this.state.password !== this.state.confirmPassword ||
      this.state.password === "" ||
      this.state.confirmPassword === "" ||
      this.state.loading ||
      this.state.finalError ||
      !this.state.activeFingerprintId;
    return (
      <>
        {this.state.mainLoading === 1 ? (
          <div className="hourglass-loader">
            <img src="filter_hourglass.gif" alt="" />
          </div>
        ) : this.state.mainLoading === 2 ? (
          <Template>
            <div
              className="login-welcome-container"
              style={{ display: "grid" }}
            >
              <div className="header-container">
                <span className="login-welcome-text">Reset your Password</span>
              </div>
              <div className="" style={{ maxWidth: "368px", width: "100%" }}>
                {this.state.backendBroke ? (
                  <span className="error-msg" style={{ textAlign: "start" }}>
                    {backendBroke}
                  </span>
                ) : (
                  ""
                )}
              </div>
            </div>
            <br />
            <div>
              <form
                autoComplete="on"
                onSubmit={(e) => {
                  e.preventDefault();
                  this.handleSubmitLogin();
                }}
              >
                <Input
                  leftChild={<img alt="" className="icon" src={LockIcon} />}
                  rightChild={
                    <img
                      alt=""
                      onClick={this.showPassword}
                      className="icon show-password"
                      src={require(
                        `./../../assests/${this.state.inputPasswordIcon}`
                      )}
                    />
                  }
                  label="PASSWORD"
                  placeholder="Enter your password"
                  value={this.state.password}
                  validation="text"
                  errorMessage="Password should be atleast 5 characters long"
                  getInputValue={this.getPassword}
                  type={this.state.inputPasswordType}
                  error={this.state.error}
                  handleError={this.handleError}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      this.handleSubmitLogin();
                    }
                  }}
                  name={AUTH_CONSTANTS.RESET_PASSWORD_INPUT.NAME}
                  autoComplete={
                    AUTH_CONSTANTS.RESET_PASSWORD_INPUT.AUTO_COMPLETE
                  }
                />

                <Input
                  leftChild={<img alt="" className="icon" src={LockIcon} />}
                  rightChild={
                    <img
                      alt=""
                      onClick={this.showPassword}
                      className="icon show-password"
                      src={require(
                        `./../../assests/${this.state.inputPasswordIcon}`
                      )}
                    />
                  }
                  label="RE-ENTER PASSWORD"
                  placeholder="Re-enter your password"
                  value={this.state.confirmPassword}
                  validation="text"
                  errorMessage="Password should be atleast 5 characters long"
                  getInputValue={this.getConfirmPassword}
                  type={this.state.inputPasswordType}
                  error={this.state.error}
                  handleError={this.handleError}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      this.handleSubmitLogin();
                    }
                  }}
                  name={AUTH_CONSTANTS.RESET_PASSWORD_INPUT.NAME2}
                  autoComplete={
                    AUTH_CONSTANTS.RESET_PASSWORD_INPUT.AUTO_COMPLETE
                  }
                />
                {this.state.err_message && (
                  <small style={{ color: "red" }}>
                    {this.state.err_message}
                  </small>
                )}

                <div>
                  {this.state.loading ? (
                    <button type="submit" className="inactive-button" disabled>
                      Resetting..
                    </button>
                  ) : this.state.finalError ? (
                    <button type="submit" className="inactive-button" disabled>
                      Reset
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className={
                        !isResetDisabled
                          ? "without-password click-ripple-effect"
                          : "inactive-button"
                      }
                      disabled={isResetDisabled}
                    >
                      Reset
                    </button>
                  )}
                </div>
              </form>
              {this.state.passwordResetMessage !== "" && (
                <div style={{ margin: "10px" }}>
                  <small>{this.state.passwordResetMessage}</small>
                </div>
              )}

              <button
                type="button"
                disabled={!!this.state.loading}
                onClick={this.handleGoToMainLogin}
                className={
                  this.state.loading
                    ? "inactive-button-password"
                    : "go-passwordless"
                }
              >
                <img style={{ marginRight: "10px" }} src="stars.svg" alt="" />
                Go Passwordless
                <span> Never remember a password again</span>
              </button>
            </div>
            <div className="login-contact-us-container">
              <div>
                New to Stylumia?&nbsp;{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.stylumia.ai/get-a-demo/"
                >
                  {" "}
                  Sign up for a demo{" "}
                </a>
              </div>
            </div>
          </Template>
        ) : this.state.mainLoading === 3 ? (
          <div className="login_unsuccessful">
            <Lottie style={{ height: "170px" }} animationData={sadDog} loop />
            {this.state.status_code === 409 ? (
              <>
                <h2 className="login-page-message-header">
                  Different device / browser detected
                </h2>
                <p>
                  Please ensure you're opening the link on the same browser /
                  device from where it was requested.
                </p>
                <button
                  type="button"
                  onClick={this.handleRedirect}
                  className="request-again-btn click-ripple-effect"
                  style={{ backgroundColor: "#479AFF" }}
                >
                  Try Again
                </button>
              </>
            ) : this.state.status_code === 401 ||
              this.state.status_code === 400 ? (
              <>
                <h2 className="login-page-message-header">
                  Oops, link expired
                </h2>
                <p>
                  Kindly request again and click on the link within 15 minutes
                </p>
                <button
                  type="button"
                  onClick={this.handleRedirect}
                  className="request-again-btn click-ripple-effect"
                  style={{ backgroundColor: "#479AFF" }}
                >
                  Request Again
                </button>
              </>
            ) : (
              ""
            )}
          </div>
        ) : (
          ""
        )}
      </>
    );
  }
}

function CallApi(Component) {
  return function WrappedComponent(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const { getVisitorId } = useDeviceIdentification();

    return (
      <Component
        navigate={navigate}
        location={location}
        getVisitorId={getVisitorId}
        {...props}
      />
    );
  };
}

export default CallApi(ResetPassword);
